import { DateTime } from 'luxon';
import {
    GetReferral,
    GetReferralBodyPart,
    IWorklistItemDto,
    ReferralStatus,
    StatusType,
    UrgencyType,
    StrokeQueryType,
    StrokeProtocol,
    GetReferralDetails
} from '../../api-module/control-centre-api-client';
import { CommonUtilities } from '@app/shared/common-utilities';
import { BadgeClass, SortedBadges } from './css-badge-classes';
import { OrderStatusClassMapping } from './order-status-class-mappings';
import { ReferralStatusClassMapping } from './referral-status-class-mapping';
import { ImagingRequestStatus, ImagingRequestType, ScanRegionStatus } from './enums';
import { ReferralsUtility } from '@app/referrals/referrals.utility';

export class ImagingRequest {
    id: string;
    status: ImagingRequestStatus;
    type: ImagingRequestType;

    referralId?: number;

    patientMrn: string;
    patientFirstName: string;
    patientLastName: string;
    patientLocation: string;
    patientDob: string;

    siteName: string;

    referringDoctor: string;
    referringDoctorPhone: string;

    urgency: UrgencyType;
    isSimpleStroke: boolean = false;
    isThrombectomy: boolean = false;
    isThrombolysis: boolean = false;
    isUnspecifiedStroke: boolean = false;

    scanRegions: ScanRegion[] = [];
    headerBadgeClasses: string[];

    editableRequest: boolean;

    private readonly _initialReferral: GetReferral | GetReferralDetails;
    private readonly _initialOrders: IWorklistItemDto[];

    mostRecentCreatedAt: DateTime;

    constructor(referral?: GetReferral | GetReferralDetails, orders?: IWorklistItemDto[]) {
        this._initialReferral = referral;
        this._initialOrders = orders ?? [];

        if (referral) {
            this.type = orders?.length > 0 ? ImagingRequestType.ReferralWithOrders : ImagingRequestType.ReferralWithoutOrders;
        } else if (orders?.length > 0) {
            this.type = ImagingRequestType.OrderWithoutReferral;
        } else {
            console.error('Invalid arguments passed through to constructor - no referral or orders.');
            this.type = ImagingRequestType.Invalid;
            return;
        }

        // Basic details
        switch (this.type) {
            case ImagingRequestType.ReferralWithOrders:
            case ImagingRequestType.ReferralWithoutOrders:
                this.id = referral.id?.toString();
                this.referralId = referral.id;

                this.patientMrn = referral.patient?.patientExternalId;
                this.patientFirstName = referral.patient?.firstName?.trim();
                this.patientLastName = referral.patient?.lastName?.trim();
                this.patientLocation = referral.patient?.location;
                this.patientDob = referral.patient?.dateOfBirth;

                this.siteName = referral.siteName;

                this.referringDoctor = referral.referrerName;
                this.referringDoctorPhone = referral.referrerPhone;

                break;
            case ImagingRequestType.OrderWithoutReferral: {
                const firstOrder = orders[0];
                this.id = firstOrder.accessionNumber;

                this.patientMrn = firstOrder.mrn;
                this.patientFirstName = firstOrder.patientFirstName?.trim();
                this.patientLastName = firstOrder.patientLastName?.trim();
                this.patientLocation = firstOrder.patientLocation;
                this.patientDob = firstOrder.patientBirthDate;

                this.siteName = firstOrder.siteName;

                this.referringDoctor = firstOrder.referringDoctor;
                this.referringDoctorPhone = firstOrder.referringDoctorPhone;

                break;
            }
        }

        this.editableRequest =
            this.type === ImagingRequestType.ReferralWithoutOrders &&
            referral.status !== ReferralStatus.Cancelled &&
            (!referral.assignee || referral.status === ReferralStatus.OnHold);

        const uncancelledOrders = orders ? orders.filter(o => getImagingRequestStatusFromOrder(o) != ImagingRequestStatus.Cancelled) : [];
        const hasUncancelledReferral = referral ? getReferralStatus(referral) != ImagingRequestStatus.Cancelled : false;
        const hasUncancelledOrders = uncancelledOrders.length > 0;
        let urgencies = new Array<UrgencyType>();

        // Determine urgency && status
        if (hasUncancelledOrders) {
            this.status = getFirstMatchingOrderStatus(uncancelledOrders);
            urgencies = uncancelledOrders.map(order => order.urgency);
            if (hasUncancelledReferral) {
                urgencies.push(referral.urgency);
            }
        } else if (referral) {
            this.status = getReferralStatus(referral);
            urgencies.push(referral.urgency);
        } else {
            this.status = getFirstMatchingOrderStatus(orders);
            urgencies = orders.map(order => order.urgency);
        }
        this.urgency = getCombinedUrgency(urgencies);

        this.setupStrokeTypes(referral, orders);

        // Scan Regions
        this.scanRegions = getScanRegions(referral, orders);

        // Most recent created at time
        this.mostRecentCreatedAt = getMostRecentCreatedTime(referral, orders);

        this.headerBadgeClasses = getSortedHeaderBadges(this.urgency, referral, orders);
    }

    /**
     * Will return a new ImagingRequest, containing the changes from the given referral and orders if
     * any are more recent than the referral & orders this was initially created with.
     * If deletedLinkedOrderIds is supplied it will remove existing orders that were unlinked
     */
    mergeWithChanges(
        changedReferral: GetReferral | GetReferralDetails | null,
        changedOrders: IWorklistItemDto[],
        deletedLinkedOrderIds: Set<number> | null = null
    ): {
        imagingRequest: ImagingRequest;
        referralChanged: boolean;
        updatedOrderAccessions: string[];
        removedOrderAccessions: Set<string>;
    } {
        const changedReferralIsMoreRecent =
            changedReferral &&
            (!this._initialReferral ||
                CommonUtilities.dateTimeIsEqualOrMoreRecent(changedReferral.updatedAt, this._initialReferral.updatedAt));
        const referral = changedReferralIsMoreRecent ? changedReferral : this._initialReferral;

        const removedOrderAccessions = new Set<string>();
        let orders: IWorklistItemDto[] = [];

        if (deletedLinkedOrderIds?.size > 0) {
            // matching on deletedLinkedOrderIds isn't enough because the order may be re-linked later
            const presentDeletedLinkedOrderAccessions = new Set<string>(
                referral?.linkedOrders?.filter(lo => deletedLinkedOrderIds.has(lo.id)).map(o => o.accessionNumber) ?? []
            );

            this._initialOrders.forEach(order => {
                if (presentDeletedLinkedOrderAccessions.has(order.accessionNumber)) {
                    removedOrderAccessions.add(order.accessionNumber);
                } else {
                    orders.push(order);
                }
            });
        } else {
            orders = [...this._initialOrders];
        }

        const updatedOrderAccessions: string[] = [];
        changedOrders?.forEach(changedOrder => {
            const indexOfInitialOrder = orders.findIndex(order => order.accessionNumber === changedOrder.accessionNumber);
            if (indexOfInitialOrder !== -1) {
                const initialOrder = orders[indexOfInitialOrder];
                const changedOrderIsMoreRecent = CommonUtilities.dateTimeIsEqualOrMoreRecent(
                    changedOrder.lastUpdatedAt,
                    initialOrder.lastUpdatedAt
                );
                if (changedOrderIsMoreRecent) {
                    orders.splice(indexOfInitialOrder, 1, changedOrder);
                    updatedOrderAccessions.push(changedOrder.accessionNumber);
                }
            } else {
                orders.push(changedOrder);
                updatedOrderAccessions.push(changedOrder.accessionNumber);
            }
        });

        return {
            imagingRequest: new ImagingRequest(referral, orders),
            referralChanged: changedReferralIsMoreRecent,
            updatedOrderAccessions: updatedOrderAccessions,
            removedOrderAccessions: removedOrderAccessions
        };
    }

    setupStrokeTypes(referral?: GetReferral | GetReferralDetails, orders?: IWorklistItemDto[]) {
        if (referral) {
            switch (referral.strokeQueryType) {
                case StrokeQueryType.SimpleStroke:
                    this.isSimpleStroke = true;
                    break;
                case StrokeQueryType.Thrombectomy:
                    this.isThrombectomy = true;
                    break;
                case StrokeQueryType.Thrombolysis:
                    this.isThrombolysis = true;
                    break;
                case StrokeQueryType.Unspecified:
                    this.isUnspecifiedStroke = true;
                    break;
            }
        }

        if (orders?.length > 0) {
            orders.forEach(order => {
                switch (order.strokeProtocolType) {
                    case StrokeProtocol.SimpleStroke:
                        this.isSimpleStroke = true;
                        break;
                    case StrokeProtocol.Thrombectomy:
                        this.isThrombectomy = true;
                        break;
                    case StrokeProtocol.Thrombolysis:
                        this.isThrombolysis = true;
                        break;
                    case StrokeProtocol.Unspecified:
                        this.isUnspecifiedStroke = true;
                        break;
                }
            });
        }
    }
}

function getScanRegions(referral?: GetReferral | GetReferralDetails, orders?: IWorklistItemDto[]): ScanRegion[] {
    let requestedScanRegions: ReferralScanRegion[] = [];
    let finalisedScanRegions: ReferralScanRegion[] = [];
    let orderScanRegions: OrderScanRegion[] = [];
    let hasDecision: boolean = false;

    if (referral) {
        [requestedScanRegions, finalisedScanRegions, hasDecision] = getScanRegionFromReferral(referral);
    }

    if (orders?.length > 0) {
        orderScanRegions = orders.map(getScanRegionFromOrder);
    }

    // Deduplicate scan regions
    if (hasDecision) {
        const indexesToRemove: number[] = [];
        requestedScanRegions.forEach((requestedScanRegion, index) => {
            const matchingBodyPartInDecision = finalisedScanRegions.find(finalisedScanRegion =>
                scanRegionMatches(requestedScanRegion, finalisedScanRegion)
            );
            if (!matchingBodyPartInDecision) {
                requestedScanRegion.status = ScanRegionStatus.Declined;
            } else {
                indexesToRemove.push(index);
            }
        });

        indexesToRemove.reverse().forEach(scanRegionToRemove => {
            requestedScanRegions.splice(scanRegionToRemove, 1);
        });
    }

    return [...requestedScanRegions, ...finalisedScanRegions, ...orderScanRegions];
}

function getScanRegionFromReferral(referral: GetReferral | GetReferralDetails): [ReferralScanRegion[], ReferralScanRegion[], boolean] {
    const referralScanRegionStatus =
        getReferralStatus(referral) === ImagingRequestStatus.Cancelled ? ScanRegionStatus.Cancelled : ScanRegionStatus.Requested;

    const requestedBodyParts = ReferralsUtility.getRequestedBodyParts(referral);
    const requestedScanRegions =
        requestedBodyParts?.map(bodyPart => getScanRegionFromBodyPart(bodyPart, referralScanRegionStatus, false)) ?? [];
    const hasDecision = !!ReferralsUtility.getTimeOfDecision(referral);
    let finalisedScanRegions = [];
    if (hasDecision) {
        let decisionStatus: string;
        if (referralScanRegionStatus === ScanRegionStatus.Cancelled) {
            decisionStatus = ScanRegionStatus.Cancelled;
        } else {
            decisionStatus = referral.status === ReferralStatus.Declined ? ScanRegionStatus.Declined : ScanRegionStatus.Approved;
        }

        const finalisedBodyParts = ReferralsUtility.getFinalisedBodyParts(referral);
        const hasDecisionBodyParts = finalisedBodyParts?.length > 0;

        finalisedScanRegions = (hasDecisionBodyParts ? finalisedBodyParts : requestedBodyParts).map(bodyPart =>
            referral instanceof GetReferralDetails
                ? getScanRegionFromBodyPart(bodyPart, decisionStatus, hasDecisionBodyParts, referral)
                : getScanRegionFromBodyPart(bodyPart, decisionStatus, hasDecisionBodyParts)
        );
    }
    return [requestedScanRegions, finalisedScanRegions, hasDecision];
}

function getScanRegionFromBodyPart(
    bodyPart: GetReferralBodyPart,
    status: ScanRegionStatus,
    fromDecision: boolean,
    referral?: GetReferralDetails
): ReferralScanRegion {
    let scanNotes: string;
    if (!bodyPart.scanNotes && referral && referral?.decisions) {
        const matchingDecisionItem = ReferralsUtility.getLatestDecision(referral.decisions)?.decisionItems?.find(item =>
            scanRegionMatches(
                {
                    bodyPart: bodyPart.bodyPart,
                    modality: bodyPart.modality,
                    contrast: bodyPart.phaseOfContrast,
                    id: 0,
                    fromDecision: false,
                    status: ''
                },
                {
                    bodyPart: item.bodyPart,
                    modality: item.modality,
                    contrast: item.phaseOfContrast,
                    id: 0,
                    fromDecision: false,
                    status: ''
                }
            )
        );
        scanNotes = matchingDecisionItem?.scanNotes;
    }
    return {
        status,
        fromDecision,
        id: bodyPart.id,
        modality: bodyPart.modality,
        bodyPart: bodyPart.bodyPart,
        contrast: bodyPart.phaseOfContrast,
        scanNotes: scanNotes ?? bodyPart.scanNotes
    };
}

function getScanRegionFromOrder(order: IWorklistItemDto): OrderScanRegion {
    let status: ScanRegionStatus;
    switch (order.status) {
        case StatusType.Reported:
            status = ScanRegionStatus.Completed;
            break;
        case StatusType.Cancelled:
            status = ScanRegionStatus.Cancelled;
            break;
        case StatusType.OnHoldMissingClinicalHistory:
        case StatusType.OnHoldMissingDocumentation:
        case StatusType.OnHoldMissingImages:
        case StatusType.OnHoldMissingPriors:
        case StatusType.OnHoldMissingPriorImages:
        case StatusType.OnHoldReviewOrderDetails:
            status = ScanRegionStatus.ActionNeeded;
            break;
        default:
            status = ScanRegionStatus.OrderPending;
            break;
    }

    return {
        status: status,
        modality: order.modality,
        bodyPart: order.studyDescription,
        accessionNumber: order.accessionNumber
    };
}

function scanRegionMatches(left: ReferralScanRegion, right: ReferralScanRegion): boolean {
    return (
        left.modality === right.modality &&
        left.bodyPart === right.bodyPart &&
        (!left.contrast || !right.contrast || left.contrast === right.contrast)
    );
}

function getSortedHeaderBadges(urgency: UrgencyType, referral?: GetReferral | GetReferralDetails, orders?: IWorklistItemDto[]): string[] {
    const headerBadges = [];
    if (urgency === UrgencyType.TraumaAndStroke || urgency === UrgencyType.Stroke) {
        headerBadges.push(BadgeClass.Stroke);
    }
    if (urgency === UrgencyType.TraumaAndStroke || urgency === UrgencyType.Trauma) {
        headerBadges.push(BadgeClass.Trauma);
    }
    if (orders?.length > 0) {
        const orderStatuses = new Set(orders.map(order => order.status));
        headerBadges.push(...[...orderStatuses].map(orderStatus => OrderStatusClassMapping.get(orderStatus)));
    } else if (referral) {
        const hasDecision = !!ReferralsUtility.getTimeOfDecision(referral);
        let decisionClass: BadgeClass;
        if (hasDecision) {
            switch (referral.status) {
                case ReferralStatus.Cancelled:
                    decisionClass = BadgeClass.Cancelled;
                    break;
                case ReferralStatus.Declined:
                    decisionClass = BadgeClass.Declined;
                    break;
                default:
                    decisionClass = BadgeClass.Approved;
            }
            headerBadges.push(decisionClass);
        }
        if (![ReferralStatus.Cancelled, ReferralStatus.Declined].includes(referral.status)) {
            headerBadges.push(ReferralStatusClassMapping.get(referral.status));
        }
    }
    return headerBadges
        .filter(badgeValue => !!badgeValue)
        .sort((badgeA, badgeB) => SortedBadges.indexOf(badgeA) - SortedBadges.indexOf(badgeB));
}

function getReferralStatus(referral: GetReferral | GetReferralDetails): ImagingRequestStatus {
    switch (ReferralStatus[referral.status]) {
        case ReferralStatus.RequiresFollowUp:
        case ReferralStatus.FollowUpInProgress:
        case ReferralStatus.OnHold:
        case ReferralStatus.ClinicalDiscussionRequired:
            return ImagingRequestStatus.ActionNeeded;
        case ReferralStatus.New:
        case ReferralStatus.AwaitingAssignment:
        case ReferralStatus.AwaitingDecision:
            return ImagingRequestStatus.ReferralInProgress;
        case ReferralStatus.AwaitingAcknowledgement:
        case ReferralStatus.FollowedUp:
        case ReferralStatus.Declined:
        case ReferralStatus.Acknowledged:
        case ReferralStatus.Deferred:
            return ImagingRequestStatus.ApprovedOrDeclined;
        case ReferralStatus.Cancelled:
            return ImagingRequestStatus.Cancelled;
        default:
            console.error('Unrecognised referral status: ' + referral.status);
            return ImagingRequestStatus.Unknown;
    }
}

function getImagingRequestStatusFromOrder(order: IWorklistItemDto): ImagingRequestStatus {
    switch (order.status) {
        case StatusType.OnHoldMissingClinicalHistory:
        case StatusType.OnHoldMissingImages:
        case StatusType.OnHoldMissingDocumentation:
        case StatusType.OnHoldMissingPriors:
        case StatusType.OnHoldMissingPriorImages:
        case StatusType.OnHoldReviewOrderDetails:
            return ImagingRequestStatus.ActionNeeded;
        case StatusType.AwaitingFurtherDetails:
        case StatusType.AwaitingImages:
        case StatusType.TransferringImages:
            return ImagingRequestStatus.ReceivingOrderDetails;
        case StatusType.ReadyToReport:
        case StatusType.Reporting:
            return ImagingRequestStatus.ReadyToReport;
        case StatusType.Reported:
            return ImagingRequestStatus.ReportCompleted;
        case StatusType.Cancelled:
            return ImagingRequestStatus.Cancelled;
        default:
            return ImagingRequestStatus.Unknown;
    }
}

function getCombinedUrgency(urgencies: UrgencyType[]): UrgencyType {
    const urgencyTypes = new Set<UrgencyType>(urgencies);
    if (urgencyTypes.has(UrgencyType.TraumaAndStroke)) {
        return UrgencyType.TraumaAndStroke;
    } else if (urgencyTypes.has(UrgencyType.Trauma)) {
        return urgencyTypes.has(UrgencyType.Stroke) ? UrgencyType.TraumaAndStroke : UrgencyType.Trauma;
    } else if (urgencyTypes.has(UrgencyType.Stroke)) {
        return UrgencyType.Stroke;
    }

    return UrgencyType.Routine;
}

function getFirstMatchingOrderStatus(orders: IWorklistItemDto[]): ImagingRequestStatus {
    const orderStatuses = orders.map(getImagingRequestStatusFromOrder);
    const leftMostStatus = sortedImagingRequestStatus.find(status => orderStatuses.includes(status));
    if (leftMostStatus === ImagingRequestStatus.Unknown) {
        console.error('None of the order statuses belong to any category.', orderStatuses);
    }
    return leftMostStatus;
}

function getMostRecentCreatedTime(referral?: GetReferral | GetReferralDetails, orders?: IWorklistItemDto[]): DateTime {
    let mostRecentCreatedTime = DateTime.fromMillis(0); // minimum possible value

    if (referral) {
        mostRecentCreatedTime = referral.createdAt ? DateTime.fromISO(referral.createdAt) : DateTime.now();
    }

    if (orders) {
        mostRecentCreatedTime = DateTime.max(
            mostRecentCreatedTime,
            ...orders.map(o => (o.createdAt ? DateTime.fromISO(o.createdAt) : DateTime.now()))
        );
    }

    return mostRecentCreatedTime;
}

export type ScanRegion = ReferralScanRegion | OrderScanRegion;

export interface ReferralScanRegion extends BaseScanRegion {
    id: number;
    fromDecision: boolean;
    contrast?: string;
    scanNotes?: string;
}
export function isReferralScanRegion(scanRegion: ScanRegion): scanRegion is ReferralScanRegion {
    return (scanRegion as ReferralScanRegion).id !== undefined;
}

export interface OrderScanRegion extends BaseScanRegion {
    accessionNumber: string;
}
export function isOrderScanRegion(scanRegion: ScanRegion): scanRegion is OrderScanRegion {
    return (scanRegion as OrderScanRegion).accessionNumber !== undefined;
}

interface BaseScanRegion {
    status: ScanRegionStatus;
    modality: string;
    bodyPart: string;
}

const sortedImagingRequestStatus = [
    ImagingRequestStatus.ActionNeeded,
    ImagingRequestStatus.ReferralInProgress,
    ImagingRequestStatus.ApprovedOrDeclined,
    ImagingRequestStatus.ReceivingOrderDetails,
    ImagingRequestStatus.ReadyToReport,
    ImagingRequestStatus.ReportCompleted,
    ImagingRequestStatus.Cancelled
];
